import {computed, inject, Injectable, Signal, signal, WritableSignal} from '@angular/core';
import {ActivatedRoute, NavigationEnd, NavigationStart, Router} from '@angular/router';
import {VaultService} from '../vault/vault.service';
import {MagnetService} from '../magnet/magnet.service';
import {DocumentService} from '../document/document.service';
import {VaultQuery} from '../../queries/vault.query';
import {TaskService} from '../task/task.service';
import {TaskQuery} from '../../queries/task.query';
import {UserService} from '../user/user.service';
import {UserQuery} from '../../queries/user.query';
import {CheckedOutDocumentService} from '../checked-out-document/checked-out-document.service';
import {AppService} from '../app/app.service';
import {NavigationService} from '../navigation/navigation.service';
import {HistoryService} from '../history/history.service';
import {UrlParameters} from '../../models/url/url-parameters.model';
import {getUrlParameters} from '../../util/get-url-parameters.util';
import {UserGroupService} from '../user-group/user-group.service';
import {BrowserQuery} from '../../queries/browser.query';
import {NotificationService} from '../notification/notification.service';
import {SearchScopeEnum} from '../../enums/search-scope.enum';
import {URL_PATH_CONFIGS} from '../../../configs/url-path.config';
import {PanelService} from '../panel/panel.service';
import {PanelLocationEnum} from '../../enums/panel-location.enum';

@Injectable({
    providedIn: 'root'
})
export class UrlParamsObserverService {
    private readonly panelService = inject(PanelService);
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly appService = inject(AppService);
    private readonly browserQuery = inject(BrowserQuery);
    private readonly checkedOutDocumentService = inject(CheckedOutDocumentService);
    private readonly documentService = inject(DocumentService);
    private readonly historyService = inject(HistoryService);
    private readonly magnetService = inject(MagnetService);
    private readonly navigationService = inject(NavigationService);
    private readonly notificationService = inject(NotificationService);
    private readonly router = inject(Router);
    private readonly taskQuery = inject(TaskQuery);
    private readonly taskService = inject(TaskService);
    private readonly userGroupService = inject(UserGroupService);
    private readonly userQuery = inject(UserQuery);
    private readonly userService = inject(UserService);
    private readonly vaultQuery = inject(VaultQuery);
    private readonly vaultService = inject(VaultService);
    private readonly currentParamsSignal: WritableSignal<UrlParameters> = signal({});
    private readonly tasksUrlPathName = 'tasks';
    private readonly taskUrlPathName = 'task';
    private readonly meUrlPathName = 'me';
    private readonly loginUrlPath = 'auth/login';
    private readonly solutionStoreUrlPathName = 'solution-store';
    private readonly vaultsUrlPathName = 'vaults';
    private readonly urlSeparator = '/';
    private readonly amagnoLinkUrlPathName = 'amagno-link';

    public setup(): void {
        const getParams = (): void => {
            getUrlParameters(this.activatedRoute)
                .then(async (params: UrlParameters) => {
                    await this.handleVaultIdParam(params.vaultId);
                    await this.handleDocumentIdParam(params.documentId);
                    await this.handleDocumentIdParam(params.checkedOutDocumentId, true);
                    await this.handleMagnetIdParam(params.magnetId);
                    await this.handleUserIdParam(params.userId);
                    this.handleTaskVaultIdParam(params.taskVaultId);
                    this.handleTaskIdParam(params.taskId);
                    this.handleGroupIdParam(params.groupId);

                    this.currentParamsSignal.set(params);

                    const url = this.router.url;
                    await this.updateSearchTaskScopeByCurrentUrl(url);

                    if (url.includes(this.loginUrlPath)) {
                        this.notificationService.setHasReadNotificationInSession(false);
                    }
                });
        };

        // Hack: Without this call, the getParams-Method would not be triggered on application start. Therefore, we need to call it within the setup.
        // https://github.com/angular/angular/issues/17473#issuecomment-308848113
        getParams();

        this.router.events.subscribe(event => {
            if (event instanceof NavigationStart) {
                this.appService.setHasActionButton(false);
            }
            if (event instanceof NavigationEnd) {
                getParams();
            }
        });
    }

    public getCurrentDocumentId(): string | undefined {
        return this.currentParamsSignal().documentId ?? this.currentParamsSignal().checkedOutDocumentId;
    }

    public getCurrentDocumentIdSignal(): Signal<string | undefined> {
        return computed(() => this.currentParamsSignal().documentId ?? this.currentParamsSignal().checkedOutDocumentId);
    }

    public isShowingDocumentPreview(): boolean {
        const urlParts = this.router.url.split('/');
        return urlParts.at(-2) === 'document' || urlParts.at(-2) === 'favorite-document';
    }

    public isShowingAmagnoLink(): boolean {
        return this.router.url.includes(this.urlSeparator + this.amagnoLinkUrlPathName + this.urlSeparator);
    }

    private async updateSearchTaskScopeByCurrentUrl(url: string): Promise<void> {
        const activeVaultId = this.vaultQuery.getFirstActiveId() as string;
        const isGlobalScope = await this.checkIfIsGlobalScope(url, activeVaultId);
        this.panelService.closePanel(PanelLocationEnum.Sidebar);
        this.appService.setSearchTaskGlobalScope(isGlobalScope);
    }

    private handleGroupIdParam(groupId?: string): void {
        this.userGroupService.setActiveGroup(groupId ? [groupId] : []);
    }

    private handleTaskIdParam(taskId?: string): void {
        if (!taskId) {
            this.taskService.setSelectedMagnetTask(undefined);
            return;
        }

        if (this.taskQuery.getSelectedMagnetTaskId() !== taskId) {
            this.taskService.setSelectedMagnetTask(taskId);
        }
    }

    private handleTaskVaultIdParam(taskVaultId?: string): void {
        if (!taskVaultId) {
            this.taskService.setSelectedMagnetTask(undefined);
            this.vaultService.setParamVaultId(undefined);
            return;
        }

        this.vaultService.setParamVaultId(taskVaultId);
        if (this.taskQuery.getFirstActiveId() !== taskVaultId) {
            this.taskService.setActiveTaskById(taskVaultId);
        }
    }

    private async handleUserIdParam(userId?: string): Promise<void> {
        if (!userId) {
            this.userService.setSelectedUser('');
            return;
        }

        if (!(this.userQuery.hasEntity(userId))) {
            await this.userService.fetchUsers();
        }
        this.userService.setSelectedUser(userId);
    }

    private async handleMagnetIdParam(magnetId?: string): Promise<void> {
        if (!magnetId) {
            this.magnetService.setSelectedMagnet();
            return;
        }

        if (this.magnetService.getSelectedMagnetId() === magnetId) {
            return;
        }

        try {
            const magnet = await this.magnetService.fetchAndGetMagnet(magnetId);
            this.magnetService.setSelectedMagnet(magnet);
        } catch {
            this.navigationService.goBackInHistory();
        }
    }

    private async handleDocumentIdParam(documentId?: string, isCheckedOutDocumentId: boolean = false): Promise<void> {
        if (!documentId) {
            return;
        }

        const document = await this.documentService.fetchAndGetDocument(documentId);
        if (!document) {
            this.documentService.unsetSelectedDocuments();
            return;
        }

        await this.documentService.setSelectedDocument(documentId, true);
        await this.vaultService.setActiveVault(document.vaultId);
        if (isCheckedOutDocumentId) {
            await this.checkedOutDocumentService.setActive(documentId);
        }
    }

    private async handleVaultIdParam(vaultId?: string): Promise<void> {
        if (!vaultId) {
            this.vaultService.setParamVaultId(undefined);
            return;
        }

        this.vaultService.setParamVaultId(vaultId);
        if (!this.vaultQuery.hasEntity(vaultId)) {
            const vault = await this.vaultService.fetchAndGetVaultById(vaultId);
            if (vault) {
                await this.vaultService.setActiveVault(vault.id);
            } else {
                this.historyService.removeHistoryItemsWithPathContaining(vaultId);
                this.navigationService.navigate([
                        this.meUrlPathName,
                        this.vaultsUrlPathName
                    ])
                    .then();
            }

            return;
        }

        if (vaultId !== this.vaultQuery.getFirstActiveId()) {
            await this.vaultService.setActiveVault(vaultId);
        }
    }

    private async checkIfIsGlobalScope(url: string, activeVaultId?: string): Promise<boolean> {
        const hasSmallLayout = this.browserQuery.hasSmallViewport();
        const params = this.currentParamsSignal();

        if (url.startsWith(this.urlSeparator + URL_PATH_CONFIGS.paths.search) && params.searchScope) {
            return params.searchScope === SearchScopeEnum.GLOBAL;
        }

        if (url.startsWith(this.urlSeparator + URL_PATH_CONFIGS.paths.searchQuery)) {
            return false;
        }

        if (url.startsWith(this.urlSeparator + URL_PATH_CONFIGS.paths.favorites) || url.startsWith(this.urlSeparator + URL_PATH_CONFIGS.paths.lastEdited) ||
            url.startsWith(this.urlSeparator + URL_PATH_CONFIGS.paths.newestDocuments)) {
            return true;
        }

        if (url.includes(this.urlSeparator + this.tasksUrlPathName)) {
            if (url.includes(this.urlSeparator + this.taskUrlPathName + this.urlSeparator) || (hasSmallLayout && !!this.currentParamsSignal().taskVaultId)) {
                await this.handleVaultIdParam(this.currentParamsSignal().taskVaultId);
                return false;
            }
            return true;
        }

        return url.includes(this.urlSeparator + this.solutionStoreUrlPathName)
            || url.includes(this.urlSeparator + this.meUrlPathName)
            || url.includes(this.urlSeparator + this.amagnoLinkUrlPathName)
            || !activeVaultId;
    }
}
